import React, { useState } from 'react';
import { Datagrid, DateField, FunctionField, List, TextField, TextInput, TopToolbar, usePermissions, useRedirect, useTranslate } from 'react-admin';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField as InputField } from '@mui/material';
import { Add } from '@mui/icons-material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { hasPermission } from '../../utils/hasPermission';
import { errorLine } from '../kiosk-releases/labels';
import type { KioskGroup } from '../kiosk-releases/types';

export const KIOSK_GROUPS_BASE = '/api/backend/kiosk-groups';

export const NewGroupDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const g = (key: string) => translate(`resources.kiosk-groups.${key}`);
  const close = () => {
    setName('');
    setDescription('');
    setProblem(null);
    onClose();
  };
  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const answer = await apiClient.post<{ data: KioskGroup }>(KIOSK_GROUPS_BASE, { name: name.trim(), description: description.trim() === '' ? null : description.trim() });
      close();
      redirect('show', 'kiosk-groups', answer.data.id);
    } catch (error) {
      setProblem(errorLine(error as ApiError, translate));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={() => !busy && close()} maxWidth="xs" fullWidth>
      <DialogTitle>{g('actions.new')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <InputField label={g('fields.name')} value={name} onChange={(event) => setName(event.target.value)} inputProps={{ maxLength: 100, 'data-testid': 'group-name' }} />
          <InputField label={g('fields.description')} value={description} onChange={(event) => setDescription(event.target.value)} multiline minRows={2} inputProps={{ maxLength: 500, 'data-testid': 'group-description' }} />
          {problem !== null && <Alert severity="error">{problem}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={busy || name.trim() === ''} sx={{ textTransform: 'none' }}>{g('actions.create')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const Actions = ({ onNew }: { onNew: () => void }) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  if (!hasPermission(permissions as string[] | null | undefined, 'kiosk-releases:write:all')) return <TopToolbar />;
  return (
    <TopToolbar>
      <Button variant="contained" startIcon={<Add />} onClick={onNew} sx={{ textTransform: 'none' }}>{translate('resources.kiosk-groups.actions.new')}</Button>
    </TopToolbar>
  );
};

export const KioskGroupsList = () => {
  const translate = useTranslate();
  const [creating, setCreating] = useState(false);
  const f = (key: string) => translate(`resources.kiosk-groups.fields.${key}`);
  return (
    <>
      <List title={translate('resources.kiosk-groups.name')} filters={[<TextInput key="q" source="q" label={f('name')} alwaysOn />]} actions={<Actions onNew={() => setCreating(true)} />} sort={{ field: 'name', order: 'ASC' }} exporter={false}>
        <Datagrid bulkActionButtons={false} rowClick="show">
          <FunctionField
            label={f('name')}
            render={(record: KioskGroup) => (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>{record.name}</span>
                {record.isTestGroup && <Chip size="small" color="warning" label={translate('resources.kiosk-groups.testGroup')} />}
              </Box>
            )}
          />
          <TextField source="description" label={f('description')} emptyText="-" />
          <TextField source="memberCount" label={f('memberCount')} />
          <DateField source="updatedAt" label={f('updatedAt')} showTime />
        </Datagrid>
      </List>
      <NewGroupDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
};
